import { useCallback, useSyncExternalStore } from 'react';

import type {
  PhaseBuildExecutionState,
  PhaseBuildTaskOption,
} from '../../services/buildPhaseService';
import { getPhaseBuildLabel } from '../../utils/phaseBuildPresentation';

export interface SelectionRecord {
  availableSignature: string;
  hydrated: boolean;
  revision: number;
  selectedIds: string[];
  userModified: boolean;
}

interface PhaseTaskSelection {
  active: boolean;
  hydrated: boolean;
  revision: number;
  selectedIds: string[];
  setSelectedIds: (ids: string[]) => void;
}

interface PhaseBuildSelectionPresentation {
  active: boolean;
  disabled: boolean;
  creditShortfall: number;
  label: string;
  phaseId: string | null;
  selectedTaskIds?: string[];
  disabledReason?: string;
}

const records = new Map<string, SelectionRecord>();
const listeners = new Map<string, Set<() => void>>();
const EMPTY_RECORD: SelectionRecord = {
  availableSignature: '',
  hydrated: false,
  revision: 0,
  selectedIds: [],
  userModified: false,
};

export const phaseTaskOptions = (
  state: PhaseBuildExecutionState | null | undefined,
): PhaseBuildTaskOption[] => state?.phase_tasks ?? state?.runnable_tasks ?? [];

export const isSelectablePhaseTask = (task: PhaseBuildTaskOption): boolean => (
  task.selectable !== false && task.status !== 'done'
);

export const sanitizePhaseTaskSelection = (
  tasks: PhaseBuildTaskOption[],
  selectedIds: string[],
): string[] => {
  const selectable = new Set(tasks.filter(isSelectablePhaseTask).map((task) => task.id));

  return Array.from(new Set(selectedIds)).filter((id) => selectable.has(id));
};

const selectionKey = (
  projectId?: string | null,
  phaseId?: string | null,
): string | null => (projectId && phaseId ? `${projectId}:${phaseId}` : null);

const defaultSelectedIds = (tasks: PhaseBuildTaskOption[]): string[] => (
  tasks.filter((task) => (
    isSelectablePhaseTask(task) && task.selected_by_default !== false
  )).map((task) => task.id)
);

const taskSelectionSignature = (tasks: PhaseBuildTaskOption[]): string => (
  tasks.map((task) => (
    `${task.id}:${task.status}:${task.selectable !== false}:${task.selected_by_default}`
  )).join('|')
);

// eslint-disable-next-line max-statements
const ensureRecord = (key: string, tasks: PhaseBuildTaskOption[]): SelectionRecord => {
  const availableSignature = taskSelectionSignature(tasks);
  const previous = records.get(key);

  if (!previous) {
    const created = {
      availableSignature,
      hydrated: false,
      revision: 0,
      selectedIds: defaultSelectedIds(tasks),
      userModified: false,
    };

    records.set(key, created);

    return created;
  }
  const retained = sanitizePhaseTaskSelection(tasks, previous.selectedIds);
  const retainedUnchanged = retained.length === previous.selectedIds.length
    && retained.every((id, index) => id === previous.selectedIds[index]);

  if (previous.availableSignature === availableSignature && retainedUnchanged) return previous;

  const defaults = defaultSelectedIds(tasks);
  const reconciled = {
    availableSignature,
    hydrated: previous.hydrated,
    revision: previous.revision,
    selectedIds: previous.userModified
      ? retained
      : defaults,
    userModified: previous.userModified,
  };

  records.set(key, reconciled);

  return reconciled;
};

const emit = (key: string) => listeners.get(key)?.forEach((listener) => listener());

const subscribe = (key: string | null, listener: () => void): (() => void) => {
  if (!key) return () => undefined;
  const keyListeners = listeners.get(key) ?? new Set<() => void>();

  keyListeners.add(listener);
  listeners.set(key, keyListeners);

  return () => {
    keyListeners.delete(listener);
    if (keyListeners.size === 0) listeners.delete(key);
  };
};

// Dependency closure is kept as one pure transition so checking and unchecking stay symmetric.
// eslint-disable-next-line max-statements
export const cascadePhaseTaskSelection = (
  tasks: PhaseBuildTaskOption[],
  selectedIds: string[],
  changedTaskId: string,
  checked: boolean,
): string[] => {
  const taskById = new Map(tasks.map((task) => [task.id, task]));
  const selected = new Set(selectedIds);

  if (checked) {
    const addWithDependencies = (taskId: string) => {
      const task = taskById.get(taskId);

      if (selected.has(taskId) || !task || !isSelectablePhaseTask(task)) return;
      selected.add(taskId);
      task.dependency_ids.forEach(addWithDependencies);
    };

    addWithDependencies(changedTaskId);
  } else {
    selected.delete(changedTaskId);
    let changed = true;

    while (changed) {
      changed = false;
      for (const task of tasks) {
        const missingSelectableDependency = task.dependency_ids.some((id) => {
          const dependency = taskById.get(id);

          return dependency && isSelectablePhaseTask(dependency) && !selected.has(id);
        });

        if (selected.has(task.id) && missingSelectableDependency) {
          selected.delete(task.id);
          changed = true;
        }
      }
    }
  }

  return tasks
    .filter((task) => isSelectablePhaseTask(task) && selected.has(task.id))
    .map((task) => task.id);
};

export const usePhaseTaskSelection = (
  projectId: string | null | undefined,
  phaseId: string | null | undefined,
  tasks: PhaseBuildTaskOption[],
): PhaseTaskSelection => {
  const key = selectionKey(projectId, phaseId);
  const active = Boolean(key && tasks.some(isSelectablePhaseTask));

  if (active && key) ensureRecord(key, tasks);

  const record = useSyncExternalStore(
    useCallback((listener) => subscribe(active ? key : null, listener), [active, key]),
    useCallback(() => (active && key ? records.get(key) ?? EMPTY_RECORD : EMPTY_RECORD), [active, key]),
    () => EMPTY_RECORD,
  );

  const setSelectedIds = useCallback((ids: string[]) => {
    if (!active || !key) return;
    const nextIds = sanitizePhaseTaskSelection(tasks, ids);
    const current = records.get(key);

    if (current && current.userModified && current.selectedIds.join('|') === nextIds.join('|')) return;
    records.set(key, {
      availableSignature: taskSelectionSignature(tasks),
      hydrated: true,
      revision: (current?.revision ?? 0) + 1,
      selectedIds: nextIds,
      userModified: true,
    });
    emit(key);
  }, [active, key, tasks]);

  return {
    active,
    hydrated: record.hydrated,
    revision: record.revision,
    selectedIds: record.selectedIds,
    setSelectedIds,
  };
};

/** Restore the latest server selection once. A local checkbox click always wins
 *  if it happens while the request is in flight. */
// eslint-disable-next-line max-statements
export const hydratePhaseTaskSelection = (
  projectId: string,
  phaseId: string,
  tasks: PhaseBuildTaskOption[],
  selectedIds?: string[],
): string[] => {
  const key = selectionKey(projectId, phaseId);

  if (!key) return [];
  const current = ensureRecord(key, tasks);

  if (current.userModified) return current.selectedIds;
  const hydratedIds = selectedIds === undefined
    ? current.selectedIds
    : sanitizePhaseTaskSelection(tasks, selectedIds);

  records.set(key, {
    availableSignature: taskSelectionSignature(tasks),
    hydrated: true,
    revision: current.revision,
    selectedIds: hydratedIds,
    userModified: selectedIds !== undefined,
  });
  emit(key);

  return hydratedIds;
};

export const getPhaseTaskSelection = (
  projectId: string | null | undefined,
  phaseId: string | null | undefined,
  tasks: PhaseBuildTaskOption[],
): SelectionRecord => {
  const key = selectionKey(projectId, phaseId);

  return key ? ensureRecord(key, tasks) : EMPTY_RECORD;
};

/** Test-only reset for the module-level phase selection cache. */
export const resetPhaseTaskSelectionStoreForTests = (): void => {
  records.clear();
  listeners.clear();
};

export const getPhaseSelectionCreditShortfall = (
  state: PhaseBuildExecutionState | null,
  selectedCredits: number,
): number => (
  state?.is_unlimited
    ? 0
    : Math.max(0, selectedCredits - Number(state?.available_credits ?? 0))
);

// eslint-disable-next-line max-statements
export const usePhaseBuildSelectionPresentation = (
  projectId: string | null | undefined,
  state: PhaseBuildExecutionState | null,
  loading: boolean,
): PhaseBuildSelectionPresentation => {
  const phase = state?.current_phase;
  const tasks = phaseTaskOptions(state);
  const selection = usePhaseTaskSelection(projectId, phase?.id, tasks);
  const selectedTaskIds = sanitizePhaseTaskSelection(tasks, selection.selectedIds);
  const count = selectedTaskIds.length;
  const selected = new Set(selectedTaskIds);
  const selectedCredits = tasks
    .filter((task) => selected.has(task.id))
    .reduce((total, task) => total + task.estimated_credits, 0);
  const creditShortfall = getPhaseSelectionCreditShortfall(state, selectedCredits);
  const disabledReason = count === 0
    ? 'Select at least one task to start this phase.'
    : creditShortfall > 0
      ? `Selected tasks need ${creditShortfall.toFixed(2).replace(/\.?0+$/, '')} more credits.`
      : undefined;
  const label = selection.active ? 'Build Tasks' : getPhaseBuildLabel(state, loading);

  return {
    active: selection.active,
    disabled: selection.active && Boolean(disabledReason),
    creditShortfall,
    disabledReason,
    label,
    phaseId: phase?.id ?? null,
    selectedTaskIds: selection.active ? selectedTaskIds : undefined,
  };
};
